<template>
  <div
    v-if="canViewConversation"
    class="conversation-details-wrap bg-slate-25 dark:bg-slate-800"
    :class="{
      'with-border-right': !isOnExpandedLayout,
      'h-full': isSpecialRoute,
    }"
  >
    <conversation-header
      v-if="currentChat.id"
      :chat="currentChat"
      :is-contact-panel-open="isContactPanelOpen"
      :show-back-button="isOnExpandedLayout"
      :active-status="activeStatus"
      @contact-panel-toggle="onToggleContactPanel"
    />
    <woot-tabs
      v-if="dashboardApps.length && currentChat.id"
      :index="activeIndex"
      class="dashboard-app--tabs bg-white dark:bg-slate-900 -mt-px"
      @change="onDashboardAppTabChange"
    >
      <woot-tabs-item
        v-for="tab in dashboardAppTabs"
        :key="tab.key"
        :name="tab.name"
        :show-badge="false"
      />
    </woot-tabs>
    <div
      v-show="!activeIndex"
      class="flex bg-slate-25 dark:bg-slate-800 m-0 h-full min-h-0"
    >
      <messages-view
        v-if="currentChat.id"
        :inbox-id="inboxId"
        :is-contact-panel-open="isContactPanelOpen"
        @contact-panel-toggle="onToggleContactPanel"
        @open-macros-panel="handleOpenMacrosPanel"
      />
      <div
        v-else-if="isLoadingConversation"
        class="flex-1 min-w-0 flex flex-col items-center justify-center h-full"
      >
        <woot-loading-state
          :message="$t('CONVERSATION.LOADING_CONVERSATIONS')"
        />
      </div>
      <empty-state v-else :is-on-expanded-layout="isOnExpandedLayout" />
      <!-- <ConversationSocialIcon  v-if="isMessagesViewOpen"/> -->
      <div
        v-show="showContactPanel"
        class="modal-overlay-v1"
        :class="{ 'modal-overlay-v1--fixed-embed': useFixedContactPanel }"
        @click="closeContactPanel"
      />
      <div v-show="showContactPanel" :class="contactPanelModalClasses">
        <contact-panel
          v-if="showContactPanel"
          ref="contactPanel"
          :contact="profileContact"
          :conversation-id="currentChat.id"
          :inbox-id="currentChat.inbox_id"
          :on-toggle="onToggleContactPanel"
          :open-source="panelOpenSource"
        />
      </div>
    </div>
    <dashboard-app-frame
      v-for="(dashboardApp, index) in dashboardApps"
      v-show="activeIndex - 1 === index"
      :key="currentChat.id + '-' + dashboardApp.id"
      :is-visible="activeIndex - 1 === index"
      :config="dashboardApps[index].content"
      :current-chat="currentChat"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel';
import ConversationHeader from './ConversationHeader';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import EmptyState from './EmptyState/EmptyState';
import MessagesView from './MessagesView';
// import ConversationSocialIcon from './ConversationSocialIcon';

export default {
  components: {
    ContactPanel,
    ConversationHeader,
    DashboardAppFrame,
    EmptyState,
    MessagesView,
    // ConversationSocialIcon,
  },

  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
    /**
     * When true, macros/contact sidebar uses position:fixed so it is not clipped by
     * overflow:hidden ancestors (e.g. contact profile with embedded ConversationBox).
     */
    useFixedContactPanel: {
      type: Boolean,
      default: false,
    },
    /** When embedding on contact profile, pass the page contact so sidebar/macros resolve contact id reliably. */
    profileContact: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      activeIndex: 0,
      activeStatus: '',
      panelOpenSource: 'contact_header',
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUserID: 'getCurrentUserID',
      currentUser: 'getCurrentUser',
      dashboardApps: 'dashboardApps/getRecords',
    }),
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...this.dashboardApps.map(dashboardApp => ({
          key: `dashboard-${dashboardApp.id}`,
          name: dashboardApp.title,
        })),
      ];
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
    contactPanelModalClasses() {
      const base =
        'conversation-sidebar-wrap basis-full sm:basis-[17.5rem] md:basis-[18.75rem] lg:basis-[19.375rem] xl:basis-[20.625rem] 2xl:basis-[25rem] rtl:border-r border-slate-50 dark:border-slate-700 min-h-0 z-10 flex-shrink-0 flex-grow-0 flex flex-col modal_blur';
      return [
        base,
        this.useFixedContactPanel
          ? 'open-like-modal--fixed'
          : 'open-like-modal',
      ];
    },
    isLoadingConversation() {
      const conversationId =
        this.$route.params.conversationId || this.$route.params.conversation_id;
      return conversationId && !this.currentChat.id;
    },
    isMessagesViewOpen() {
      // Check if MessagesView is visible
      return this.currentChat.id && !this.activeIndex;
    },
    isSpecialRoute() {
      const { path, query } = this.$route;
      // Apply special styles only to routes matching the pattern and exclude those with `page` query parameter
      return (
        path.startsWith('/app/accounts') &&
        path.includes('/contacts/') &&
        !query.page // Exclude routes with `page` query parameter
      );
    },
    // Resolve the contact (sender) assignee id from the current chat payload or the
    // contacts store. Used to decide whether an agent — who is not a collaborator on
    // the inbox and not the conversation assignee — should still see the admin UI
    // for every conversation belonging to their assigned contact.
    contactAssigneeId() {
      const senderAssigneeId = this.currentChat?.meta?.sender?.assignee?.id;
      if (senderAssigneeId != null && senderAssigneeId !== '') {
        return Number(senderAssigneeId);
      }
      const senderId = this.currentChat?.meta?.sender?.id;
      if (senderId == null || senderId === '') return null;
      const contact = this.$store.getters['contacts/getContact'](senderId);
      const fallback = contact?.assignee?.id;
      return fallback != null && fallback !== '' ? Number(fallback) : null;
    },
    canViewConversation() {
      if (this.currentUser.role !== 'agent') return true;
      if (Number(this.currentChat.assignee_id) === Number(this.currentUserID)) {
        return true;
      }
      return (
        this.contactAssigneeId != null &&
        this.contactAssigneeId === Number(this.currentUserID)
      );
    },
  },
  watch: {
    'currentChat.inbox_id'(inboxId) {
      if (inboxId) {
        this.$store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
      }
    },
    'currentChat.id'() {
      this.fetchLabels();
      this.activeIndex = 0;
    },
  },
  mounted() {
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
    bus.$on('update-active-status', status => {
      this.activeStatus = status;
    });
  },
  methods: {
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onToggleContactPanel() {
      this.panelOpenSource = 'contact_header';
      this.$emit('contact-panel-toggle');
    },
    closeContactPanel() {
      this.panelOpenSource = 'contact_header';
      this.$emit('contact-panel-toggle');
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },

    handleOpenMacrosPanel() {
      this.panelOpenSource = 'macros_button';
      // If the panel is already open (e.g. contact header), toggling would close it
      // instead of switching to macros — only emit when we need to open the panel.
      if (!this.isContactPanelOpen) {
        this.$emit('contact-panel-toggle');
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.conversation-details-wrap {
  @apply flex flex-col min-w-0 w-full;

  &.with-border-right {
    @apply border-r border-slate-50 dark:border-slate-700;
  }
}

.dashboard-app--tabs {
  :deep {
    .tabs-title {
      a {
        @apply pb-2 pt-1;
      }
    }
  }
}

.conversation-sidebar-wrap {
  &:deep(.contact--panel) {
    @apply w-full h-full max-w-full;
  }
}
/* Match ContactsView .special-panel-styles viewport inset (conversation contact overlay). */
.open-like-modal {
  position: fixed;
  box-shadow: 4px 4px 10px rgba(0, 0, 0, 0.5);
  top: 10vh;
  bottom: 14vh;
  width: 35%;
  height: auto;
  max-height: none;
  z-index: 40;
  left: 50%;
  transform: translateX(-50%);
  overflow: hidden !important;
}

/* Embedded contexts (contact profile): avoid clipping by overflow:hidden ancestors */
.open-like-modal--fixed {
  position: fixed;
  box-shadow: 4px 4px 10px rgba(0, 0, 0, 0.5);
  top: 10vh;
  bottom: 14vh;
  width: 35%;
  max-width: min(40rem, 92vw);
  height: auto;
  max-height: none;
  left: 50%;
  z-index: 40;
  transform: translateX(-50%);
  overflow: hidden !important;
}
.modal-overlay-v1 {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 39;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.4);
  width: 100%;
  height: 100%;
}

.modal-overlay-v1--fixed-embed {
  /* Align with ContactsView overlay (39); avoid stacking above primary sidebar. */
  z-index: 39;
}
.modal_blur {
  border-radius: 8px;
}
</style>
